import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Download, Loader2, Search, RefreshCw, User,
  Mail, Phone, Gift, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PartnerLayout from '../../components/partner/PartnerLayout';

const PartnerClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.get('/partners/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.get('/partners/clients/export', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado correctamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar datos');
    } finally {
      setExporting(false);
    }
  };

  // Filter clients by search term
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (client.user_name || '').toLowerCase().includes(search) ||
      (client.user_email || '').toLowerCase().includes(search) ||
      (client.user_dni || '').toLowerCase().includes(search) ||
      (client.benefit_title || '').toLowerCase().includes(search)
    );
  });

  // Get unique clients count (by user_id)
  const uniqueClientsCount = new Set(clients.map(c => c.user_id)).size;

  if (loading) {
    return (
      <PartnerLayout currentPage="clientes">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout currentPage="clientes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-black text-white">Mis Clientes</h1>
          <p className="text-zinc-400">Base de datos de usuarios que canjearon tus beneficios</p>
        </div>
        {clients.length > 0 && (
          <Button
            onClick={handleExportExcel}
            disabled={exporting}
            className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            data-testid="export-clients-btn"
          >
            {exporting ? (
              <Loader2 size={18} className="animate-spin mr-2" />
            ) : (
              <Download size={18} className="mr-2" />
            )}
            Descargar Excel
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-[#D4AF37]">{uniqueClientsCount}</p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Clientes Únicos</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-white">{total}</p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Canjes Totales</p>
        </div>
        <div className="hidden sm:block bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
          <p className="text-3xl font-extrabold text-green-400">
            {total > 0 ? Math.round((total / uniqueClientsCount) * 10) / 10 : 0}
          </p>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Canjes/Cliente</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, email, DNI..."
            className="pl-10 bg-zinc-900 border-zinc-800 text-white"
            data-testid="search-clients-input"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={fetchClients}
          className="border-zinc-700 text-zinc-400 hover:text-white"
          data-testid="refresh-clients-btn"
        >
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Clients List */}
      {clients.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center"
        >
          <Users size={56} className="mx-auto text-zinc-700 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Sin clientes aún</h3>
          <p className="text-zinc-500">Cuando los usuarios canjeen tus códigos, aparecerán aquí</p>
        </motion.div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          {/* Table Header - Desktop */}
          <div className="hidden lg:grid grid-cols-6 gap-4 p-4 bg-zinc-800/50 border-b border-zinc-800">
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Cliente</div>
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Email</div>
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Teléfono</div>
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">DNI</div>
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Beneficio</div>
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Fecha</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-zinc-800">
            {filteredClients.map((client, index) => (
              <motion.div
                key={client.id || index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className="p-4 hover:bg-zinc-800/30 transition-colors"
              >
                {/* Desktop View */}
                <div className="hidden lg:grid grid-cols-6 gap-4 items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                      <User size={16} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-white font-medium truncate">{client.user_name || '-'}</span>
                  </div>
                  <div className="text-zinc-400 text-sm truncate">{client.user_email || '-'}</div>
                  <div className="text-zinc-400 text-sm">{client.user_phone || '-'}</div>
                  <div className="text-zinc-400 text-sm font-mono">{client.user_dni || '-'}</div>
                  <div className="text-zinc-300 text-sm truncate">{client.benefit_title || '-'}</div>
                  <div className="text-zinc-500 text-sm">
                    {client.created_at ? new Date(client.created_at).toLocaleDateString('es-AR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    }) : '-'}
                  </div>
                </div>

                {/* Mobile View */}
                <div className="lg:hidden space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                        <User size={18} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-white font-bold">{client.user_name || 'Sin nombre'}</p>
                        <p className="text-zinc-500 text-sm">{client.user_email || '-'}</p>
                      </div>
                    </div>
                    <span className="text-zinc-600 text-xs">
                      {client.created_at ? new Date(client.created_at).toLocaleDateString('es-AR') : ''}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 pl-13">
                    {client.user_phone && (
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <Phone size={14} />
                        {client.user_phone}
                      </div>
                    )}
                    {client.user_dni && (
                      <div className="text-zinc-400 text-sm">
                        DNI: {client.user_dni}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2">
                    <Gift size={14} className="text-[#D4AF37]" />
                    <span className="text-zinc-300 text-sm">{client.benefit_title || 'Beneficio'}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Show message if no results after filter */}
          {filteredClients.length === 0 && searchTerm && (
            <div className="p-8 text-center">
              <Search size={40} className="mx-auto text-zinc-700 mb-3" />
              <p className="text-zinc-500">No se encontraron clientes con "{searchTerm}"</p>
            </div>
          )}
        </div>
      )}
    </PartnerLayout>
  );
};

export default PartnerClientsPage;
